import { Fragment, useState } from 'react';
import { StatusChip } from './Badges';
import { CreditAmount } from './CreditAmount';
import type { RatedLine, RatedUsage } from '@/api/opsTypes';
import { formatCredits } from '@/ops/credits';
import { formatMinor, formatNumber } from '@/ops/format';
import { downloadCsv, toCsv, type CsvCell } from '@/ops/csv';

/**
 * A line billed in money rather than credits: the subscription and the storage / seat overages (money
 * SKUs in the price book). Billing reports no credits for it, only `price_minor`.
 */
export function isMoneyLine(line: Pick<RatedLine, 'group' | 'credits' | 'price_minor'>): boolean {
  // By group only: a FREE usage line (a WhatsApp service message) also has 0 credits and a price of 0,
  // and is still a credits line — "EGP 0.00, money line" would be a wrong statement about it. An UNPRICED plan or
  // overage (price_minor null) is still a money line — one billing could not price ({@link isUnpricedLine}).
  return line.group === 'subscription' || line.group.endsWith('.overage');
}

/**
 * A line billing could not price (money review 2026-10-04): a plan or an overage with no price in the tenant's currency
 * (`price_minor` null), or a usage line billing explains as `unpriced: …` (postpaid usage with no credit pack priced in
 * that currency). Rendered "unpriced" — never as 0, never as a credit figure with a missing money column.
 */
export function isUnpricedLine(line: Pick<RatedLine, 'group' | 'credits' | 'price_minor' | 'explanation'>): boolean {
  if (line.price_minor !== null) return false;
  return isMoneyLine(line) || /(^|[;:]\s*)unpriced\b/i.test(line.explanation ?? '');
}

function Unpriced({ why }: { why: string }) {
  return (
    <span className="credit-amount credit-amount-block">
      <StatusChip status="unpriced" tone="danger" title={why} />
      <span className="t-caption muted">no price in this currency — not billed until one exists</span>
    </span>
  );
}

/** Margin as a share of what the tenant paid, from integers (no floating money). */
function marginShare(margin: number, credits: number): string {
  if (credits <= 0) return '—';
  return `${((margin / credits) * 100).toFixed(1)}%`;
}

/**
 * Rated lines (CONTRACTS §4.5) — the SAME table for an actual month and a projection, because the server
 * builds both with one line builder: a projection and an invoice can never disagree, and neither can
 * their tables. Credits first; the money equivalent (billing's `price_minor`) second, labelled; our cost
 * and margin after. Each line's resolved-price explanation is one click away.
 */
export function RatedLinesTable({ usage, caption }: { usage: RatedUsage; caption?: string }) {
  const [open, setOpen] = useState<Set<number>>(new Set());
  const scale = usage.credit_display_scale;

  const toggle = (index: number) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });

  if (usage.lines.length === 0) {
    return <div className="empty-state t-body">No rated usage in {usage.period}.</div>;
  }

  return (
    <div className="table-wrap">
      <table className="table">
        {caption && <caption className="sr-only">{caption}</caption>}
        <thead>
          <tr>
            <th>Line</th>
            <th className="num">Quantity</th>
            <th className="num">Credits · money equivalent</th>
            <th className="num">Our cost</th>
            <th className="num">Margin</th>
            <th aria-label="Explanation" />
          </tr>
        </thead>
        <tbody>
          {usage.lines.map((line, index) => (
            <Fragment key={`${line.group}-${index}`}>
              <tr>
                <td>
                  <div>{line.label_en}</div>
                  <div className="t-caption subtle mono">{line.group}</div>
                </td>
                <td className="num">
                  {formatNumber(line.quantity, 3)} <span className="t-caption muted">{line.unit}</span>
                </td>
                <td className="num">
                  {isUnpricedLine(line) && isMoneyLine(line) ? (
                    <Unpriced why={line.explanation} />
                  ) : isMoneyLine(line) ? (
                    // A plan or an overage is priced in MONEY (a price-book money SKU), not in credits —
                    // labelling it "at current pack price" would claim a credit conversion that never happened.
                    <span className="credit-amount credit-amount-block">
                      <span className="tabular">{formatMinor(line.price_minor, line.currency)}</span>
                      <span className="t-caption muted">money line — not paid in credits</span>
                    </span>
                  ) : (
                    <>
                      <CreditAmount micro={line.credits} scale={scale} moneyMinor={line.price_minor} currency={line.currency} />
                      {isUnpricedLine(line) && <Unpriced why={line.explanation} />}
                    </>
                  )}
                </td>
                <td className="num">{formatCredits(line.cost_credits, scale)}</td>
                <td className="num">
                  {formatCredits(line.margin_credits, scale)}
                  <div className="t-caption muted">{marginShare(line.margin_credits, line.credits)}</div>
                </td>
                <td>
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    aria-expanded={open.has(index)}
                    onClick={() => toggle(index)}
                  >
                    {open.has(index) ? 'Hide' : 'Why this price'}
                  </button>
                </td>
              </tr>
              {open.has(index) && (
                <tr>
                  <td colSpan={6} className="t-caption wrap">
                    <span className="muted">Resolved price: </span>{line.explanation || 'No explanation returned.'}
                  </td>
                </tr>
              )}
            </Fragment>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td><strong>Total</strong></td>
            <td />
            <td className="num">
              {/* The money total mixes money lines (plan, overages) with credit equivalents, so it is
                  labelled as exactly that rather than as one "at current pack price" figure. */}
              <span className="credit-amount credit-amount-block">
                <span className="tabular">{formatCredits(usage.totals.credits, scale)}</span>
                <span className="t-caption muted">
                  {formatMinor(usage.totals.price_minor, usage.currency)} money — money lines + credits at current pack price
                </span>
              </span>
            </td>
            <td className="num">{formatCredits(usage.totals.cost_credits, scale)}</td>
            <td className="num">
              {formatCredits(usage.totals.margin_credits, scale)}
              <div className="t-caption muted">{marginShare(usage.totals.margin_credits, usage.totals.credits)}</div>
            </td>
            <td />
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

/** CSV of the rated lines currently on screen — built from the live answer in memory, never stored. */
export function ratedLinesCsv(usage: RatedUsage): string {
  const header = [
    'group', 'label_en', 'quantity', 'unit', 'credits_micro', 'credits_display', 'credit_display_scale',
    'money_equivalent_minor', 'currency', 'cost_credits_micro', 'margin_credits_micro', 'explanation',
  ];
  const rows = usage.lines.map((l) => [
    l.group, l.label_en, l.quantity, l.unit, l.credits, l.credits_display, usage.credit_display_scale,
    l.price_minor, l.currency, l.cost_credits, l.margin_credits, l.explanation,
  ] as CsvCell[]);
  return toCsv([header, ...rows]);
}

/** Re-exported for the existing callers; the one implementation lives in `ops/csv`. */
export { downloadCsv };
